import React from 'react';
import { AlertTriangle, CheckCircle2, Clock, Hourglass } from 'lucide-react';
import type { Ticket } from '../../../types';
import { getTicketSla, SLA_STATE_STYLES } from './ticketSla';

/**
 * Compact SLA / waiting-time badge for ticket rows and the details panel.
 *
 * Shows e.g. "Waiting 2h · SLA in 1h", amber as the target approaches and red
 * once breached, so an admin can prioritise the queue without opening anything.
 */
export default function SlaBadge({
  ticket, dense = false, showResolved = false, urgentOnly = false,
}: { ticket: Ticket; dense?: boolean; showResolved?: boolean; urgentOnly?: boolean }) {
  const [, setTick] = React.useState(0);
  const sla = getTicketSla(ticket);

  // Re-render every 30s so a live countdown never goes stale on a long shift.
  const hasLiveClock = sla.state === 'ok' || sla.state === 'warning' || sla.state === 'breach';
  React.useEffect(() => {
    if (!hasLiveClock) return;
    const id = setInterval(() => setTick(n => n + 1), 30_000);
    return () => clearInterval(id);
  }, [hasLiveClock]);

  // Narrow surfaces (the 320px split-view list) only surface what needs action.
  if (urgentOnly && sla.state !== 'breach' && sla.state !== 'warning') return null;
  if (sla.state === 'none' && !(showResolved && sla.label)) return null;

  const style = SLA_STATE_STYLES[sla.state] ?? SLA_STATE_STYLES.none;
  const Icon = sla.state === 'breach'
    ? AlertTriangle
    : sla.state === 'warning'
      ? Clock
      : sla.state === 'waiting'
        ? Hourglass
        : CheckCircle2;

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-[6px] font-semibold whitespace-nowrap ${dense ? 'px-1.5 py-0.5 text-[11px]' : 'px-2 py-0.5 text-[10px]'}`}
      style={{ background: style.bg, color: style.text }}
      title={sla.title}
    >
      <Icon className={dense ? 'h-2.5 w-2.5' : 'h-3 w-3'} aria-hidden="true" />
      {sla.label}
    </span>
  );
}
